import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import {
  FaCheckCircle,
  FaHeart,
  FaRegHeart,
  FaReply,
  FaTrash,
  FaEdit,
  FaFlag
} from 'react-icons/fa'

import { useUser } from 'contexts/user.context'
import api from 'services/api'

import * as S from './style'

type ReactionType =
  | 'want_to_go'
  | 'been_there'
  | 'loved_it'
  | 'amazing'
  | 'saved_trip'
  | 'bucket_list'

type CommentItem = {
  id: string
  itineraryId: string
  userId: string
  parentId?: string | null
  content: string
  status: string
  editedAt?: string | null
  createdAt: string
  firstname?: string | null
  lastname?: string | null
  profilePicture?: string | null
  verified: boolean
  isItineraryAuthor: boolean
  viewerLiked: boolean
  likeCount: number
  pendingReview: boolean
  replies: CommentItem[]
}

type CommentsResponse = {
  items: CommentItem[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  commentCount: number
  reactions: {
    counts: Record<ReactionType, number>
    viewerReaction?: ReactionType | null
  }
}

type Props = {
  itineraryId: string
  itineraryAuthorId: string
  onRequireLogin: () => void
}

const reactions: Array<{
  type: ReactionType
  emoji: string
  label: string
}> = [
  { type: 'want_to_go', emoji: '✈️', label: 'Quero ir!' },
  { type: 'been_there', emoji: '📍', label: 'Já fui' },
  { type: 'loved_it', emoji: '❤️', label: 'Amei' },
  { type: 'amazing', emoji: '🤩', label: 'Que demais' },
  { type: 'saved_trip', emoji: '🧳', label: 'Salvei pra viagem' },
  { type: 'bucket_list', emoji: '🔥', label: 'Entrou na lista' }
]

const reportOptions = [
  { value: 'spam', label: 'Spam' },
  { value: 'offensive', label: 'Conteúdo ofensivo' },
  { value: 'inappropriate', label: 'Informação inadequada' },
  { value: 'other', label: 'Outro' }
]

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })

const ItineraryComments = ({
  itineraryId,
  itineraryAuthorId,
  onRequireLogin
}: Props) => {
  const { user } = useUser()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<CommentItem[]>([])
  const [commentText, setCommentText] = useState('')
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set())
  const [reportingId, setReportingId] = useState<string | null>(null)
  const [reportReason, setReportReason] = useState('spam')

  const queryKey = ['itinerary-comments', itineraryId, page, user?.id]

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await api.get<CommentsResponse>(
        `/itinerary-comments/itinerary/${itineraryId}?page=${page}&limit=10&sort=recent`
      )
      return response.data
    },
    enabled: !!itineraryId,
    staleTime: 15 * 1000
  })

  useEffect(() => {
    setPage(1)
    setItems([])
  }, [itineraryId, user?.id])

  useEffect(() => {
    if (!data) return
    setItems((current) => {
      if (page === 1) return data.items
      const ids = new Set(current.map((item) => item.id))
      return [...current, ...data.items.filter((item) => !ids.has(item.id))]
    })
  }, [data, page])

  const refresh = async () => {
    setPage(1)
    setItems([])
    await queryClient.invalidateQueries({
      queryKey: ['itinerary-comments', itineraryId]
    })
  }

  const requireLogin = () => {
    if (user?.id) return true
    onRequireLogin()
    return false
  }

  const createMutation = useMutation({
    mutationFn: async ({
      content,
      parentId
    }: {
      content: string
      parentId?: string | null
    }) =>
      api.post(`/itinerary-comments/itinerary/${itineraryId}`, {
        content,
        parentId
      }),
    onSuccess: async (response) => {
      const pending = Boolean(response.data?.pendingReview)
      toast[pending ? 'info' : 'success'](
        response.data?.message ||
          (pending
            ? 'Seu comentário está em análise.'
            : 'Comentário publicado.'),
        { theme: 'dark' }
      )
      setCommentText('')
      setReplyText('')
      setReplyTo(null)
      await refresh()
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível publicar o comentário.',
        { theme: 'dark' }
      )
    }
  })

  const editMutation = useMutation({
    mutationFn: async ({ id, content }: { id: string; content: string }) =>
      api.patch(`/itinerary-comments/${id}`, { content }),
    onSuccess: async (response) => {
      toast[response.data?.pendingReview ? 'info' : 'success'](
        response.data?.message || 'Comentário atualizado.',
        { theme: 'dark' }
      )
      setEditingId(null)
      setEditingText('')
      await refresh()
    },
    onError: (error: any) =>
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível editar o comentário.',
        { theme: 'dark' }
      )
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/itinerary-comments/${id}`),
    onSuccess: async () => {
      toast.success('Comentário excluído.', { theme: 'dark' })
      await refresh()
    },
    onError: (error: any) =>
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível excluir o comentário.',
        { theme: 'dark' }
      )
  })

  const likeMutation = useMutation({
    mutationFn: async (id: string) =>
      api.post(`/itinerary-comments/${id}/like`),
    onSuccess: async () => refresh()
  })

  const reportMutation = useMutation({
    mutationFn: async (id: string) =>
      api.post(`/itinerary-comments/${id}/report`, {
        reason: reportReason
      }),
    onSuccess: () => {
      toast.success('Denúncia enviada para análise.', { theme: 'dark' })
      setReportingId(null)
      setReportReason('spam')
    },
    onError: (error: any) =>
      toast.error(
        error?.response?.data?.message || 'Não foi possível enviar a denúncia.',
        { theme: 'dark' }
      )
  })

  const reactionMutation = useMutation({
    mutationFn: async (reactionType: ReactionType) =>
      api.put(`/itinerary-comments/itinerary/${itineraryId}/reaction`, {
        reactionType
      }),
    onSuccess: async () => refresh(),
    onError: () =>
      toast.error('Não foi possível registrar sua reação.', { theme: 'dark' })
  })

  const reactionCounts = useMemo(
    () =>
      Object.fromEntries(
        reactions.map((reaction) => [
          reaction.type,
          Number(data?.reactions?.counts?.[reaction.type] ?? 0)
        ])
      ) as Record<ReactionType, number>,
    [data?.reactions?.counts]
  )

  const sendComment = () => {
    if (!requireLogin()) return
    const value = commentText.trim()
    if (!value) return
    createMutation.mutate({ content: value })
  }

  const sendReply = (parentId: string) => {
    if (!requireLogin()) return
    const value = replyText.trim()
    if (!value) return
    createMutation.mutate({ content: value, parentId })
  }

  const toggleLike = (id: string) => {
    if (!requireLogin()) return
    likeMutation.mutate(id)
  }

  const react = (type: ReactionType) => {
    if (!requireLogin()) return
    reactionMutation.mutate(type)
  }

  const canDelete = (comment: CommentItem) =>
    Boolean(
      user?.id && (comment.userId === user.id || itineraryAuthorId === user.id)
    )

  const renderComment = (comment: CommentItem, reply = false) => {
    const own = comment.userId === user?.id
    const showAllReplies = expandedReplies.has(comment.id)
    const replies = showAllReplies
      ? comment.replies || []
      : (comment.replies || []).slice(0, 2)

    return (
      <S.CommentCard key={comment.id} $reply={reply}>
        <S.CommentHeader>
          <S.Avatar
            src={comment.profilePicture || '/images/profile-empty.png'}
            alt=""
          />
          <S.CommentIdentity>
            <strong>
              {[comment.firstname, comment.lastname]
                .filter(Boolean)
                .join(' ') || 'Viajante RedSterna'}
            </strong>
            <S.IdentityMeta>
              {comment.verified && (
                <span className="verified">
                  <FaCheckCircle /> Verificado
                </span>
              )}
              {comment.isItineraryAuthor && (
                <span className="author">Autor do roteiro</span>
              )}
              <span>{formatDate(comment.createdAt)}</span>
              {comment.editedAt && <span>editado</span>}
            </S.IdentityMeta>
          </S.CommentIdentity>
          {comment.pendingReview && <S.PendingBadge>Em análise</S.PendingBadge>}
        </S.CommentHeader>

        {editingId === comment.id ? (
          <S.EditArea>
            <textarea
              maxLength={1500}
              value={editingText}
              onChange={(event) => setEditingText(event.target.value)}
            />
            <div>
              <button
                type="button"
                onClick={() => {
                  setEditingId(null)
                  setEditingText('')
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="primary"
                disabled={editMutation.isPending || !editingText.trim()}
                onClick={() =>
                  editMutation.mutate({
                    id: comment.id,
                    content: editingText.trim()
                  })
                }
              >
                Salvar
              </button>
            </div>
          </S.EditArea>
        ) : (
          <S.CommentText>{comment.content}</S.CommentText>
        )}

        {!comment.pendingReview && (
          <S.CommentActions>
            <button type="button" onClick={() => toggleLike(comment.id)}>
              {comment.viewerLiked ? <FaHeart /> : <FaRegHeart />}
              {comment.likeCount > 0 ? comment.likeCount : 'Curtir'}
            </button>
            {!reply && (
              <button
                type="button"
                onClick={() => {
                  if (!requireLogin()) return
                  setReplyTo(replyTo === comment.id ? null : comment.id)
                  setReplyText('')
                }}
              >
                <FaReply /> Responder
              </button>
            )}
            {own && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(comment.id)
                  setEditingText(comment.content)
                }}
              >
                <FaEdit /> Editar
              </button>
            )}
            {canDelete(comment) && (
              <button
                type="button"
                onClick={() => deleteMutation.mutate(comment.id)}
                disabled={deleteMutation.isPending}
              >
                <FaTrash /> Excluir
              </button>
            )}
            {user?.id && !own && (
              <button
                type="button"
                onClick={() =>
                  setReportingId(reportingId === comment.id ? null : comment.id)
                }
              >
                <FaFlag /> Denunciar
              </button>
            )}
          </S.CommentActions>
        )}

        {reportingId === comment.id && (
          <S.ReportBox>
            <select
              value={reportReason}
              onChange={(event) => setReportReason(event.target.value)}
            >
              {reportOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={reportMutation.isPending}
              onClick={() => reportMutation.mutate(comment.id)}
            >
              Enviar denúncia
            </button>
          </S.ReportBox>
        )}

        {replyTo === comment.id && (
          <S.ReplyComposer>
            <textarea
              maxLength={1500}
              placeholder="Escreva uma resposta..."
              value={replyText}
              onChange={(event) => setReplyText(event.target.value)}
            />
            <div>
              <button
                type="button"
                onClick={() => {
                  setReplyTo(null)
                  setReplyText('')
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="primary"
                disabled={createMutation.isPending || !replyText.trim()}
                onClick={() => sendReply(comment.id)}
              >
                Responder
              </button>
            </div>
          </S.ReplyComposer>
        )}

        {!reply && replies.length > 0 && (
          <S.Replies>
            {replies.map((item) => renderComment(item, true))}
            {(comment.replies || []).length > 2 && (
              <S.MoreReplies
                type="button"
                onClick={() =>
                  setExpandedReplies((current) => {
                    const next = new Set(current)
                    if (next.has(comment.id)) next.delete(comment.id)
                    else next.add(comment.id)
                    return next
                  })
                }
              >
                {showAllReplies
                  ? 'Mostrar menos respostas'
                  : `Ver mais ${comment.replies.length - 2} respostas`}
              </S.MoreReplies>
            )}
          </S.Replies>
        )}
      </S.CommentCard>
    )
  }

  return (
    <S.Section>
      <S.ReactionPanel>
        <div>
          <strong>O que achou deste roteiro?</strong>
          <span>Interaja com um toque.</span>
        </div>
        <S.ReactionList>
          {reactions.map((reaction) => {
            const selected = data?.reactions?.viewerReaction === reaction.type
            return (
              <S.ReactionButton
                key={reaction.type}
                type="button"
                $selected={selected}
                disabled={reactionMutation.isPending}
                onClick={() => react(reaction.type)}
              >
                <span>{reaction.emoji}</span>
                <strong>{reaction.label}</strong>
                {reactionCounts[reaction.type] > 0 && (
                  <small>{reactionCounts[reaction.type]}</small>
                )}
              </S.ReactionButton>
            )
          })}
        </S.ReactionList>
      </S.ReactionPanel>

      <S.CommentsPanel>
        <S.SectionHeader>
          <div>
            <strong>Comentários ({data?.commentCount ?? 0})</strong>
            <span>Converse com outros viajantes sobre este roteiro.</span>
          </div>
        </S.SectionHeader>

        <S.Composer>
          <S.Avatar
            src={user?.profilePicture || '/images/profile-empty.png'}
            alt=""
          />
          <div>
            <textarea
              maxLength={1500}
              placeholder={
                user?.id
                  ? 'Compartilhe uma dúvida ou experiência sobre este roteiro...'
                  : 'Entre na sua conta para participar da conversa.'
              }
              value={commentText}
              onFocus={() => {
                if (!user?.id) onRequireLogin()
              }}
              onChange={(event) => setCommentText(event.target.value)}
              disabled={!user?.id}
            />
            <S.ComposerFooter>
              <small>{commentText.length}/1500</small>
              <button
                type="button"
                disabled={
                  !user?.id || !commentText.trim() || createMutation.isPending
                }
                onClick={sendComment}
              >
                Comentar
              </button>
            </S.ComposerFooter>
          </div>
        </S.Composer>

        {isLoading && page === 1 && (
          <S.State>Carregando comentários...</S.State>
        )}

        {!isLoading && items.length === 0 && (
          <S.State>
            Ainda não há comentários. Seja o primeiro a conversar sobre este
            roteiro.
          </S.State>
        )}

        <S.CommentList>
          {items.map((comment) => renderComment(comment))}
        </S.CommentList>

        {data && page < data.pagination.pages && (
          <S.LoadMore
            type="button"
            disabled={isLoading}
            onClick={() => setPage((current) => current + 1)}
          >
            {isLoading ? 'Carregando...' : 'Ver mais comentários'}
          </S.LoadMore>
        )}
      </S.CommentsPanel>
    </S.Section>
  )
}

export default ItineraryComments
